import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { BookOpen, Play, ChevronRight } from 'lucide-react'

function StudentCourseView() {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const api = apiService()

  useEffect(() => {
    fetchCourses()
    
    // Refresh courses when window regains focus (to update progress)
    const handleFocus = () => {
      fetchCourses()
    }
    
    window.addEventListener('focus', handleFocus)
    
    // Listen for custom event when a lesson is completed
    const handleLessonCompleted = (event) => {
      // Force re-render to update progress
      // Also refresh courses data to get updated lesson counts
      fetchCourses()
    }
    
    window.addEventListener('lessonCompleted', handleLessonCompleted)
    
    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('lessonCompleted', handleLessonCompleted)
    }
  }, [])

  // Get completed lessons from localStorage for a specific course
  const getCompletedLessons = (courseId) => {
    try {
      const userId = localStorage.getItem('userid')
      if (!courseId || !userId) {
        console.log('⚠️ Missing courseId or userId:', { courseId, userId })
        return []
      }
      
      // Normalize course ID to string
      const courseIdStr = String(courseId).trim()
      const key = `completedLessons_${courseIdStr}_${userId}`
      let completed = localStorage.getItem(key)
      
      // If not found, try searching all localStorage keys (in case ID format differs)
      if (!completed) {
        const allKeys = Object.keys(localStorage).filter(k => k.startsWith('completedLessons_'))
        console.log('🔍 Searching for completion data. Course ID:', courseIdStr, 'All completion keys:', allKeys)
        
        const matchingKey = allKeys.find(k => 
          k.endsWith(`_${userId}`) &&
          (k.includes(courseIdStr) || courseIdStr.includes(k.split('_')[1]))
        )
        if (matchingKey) {
          console.log('✅ Found matching key:', matchingKey)
          completed = localStorage.getItem(matchingKey)
        } else {
          console.log('❌ No matching key found for course:', courseIdStr)
        }
      } else {
        console.log('✅ Found completion data with key:', key)
      }
      
      if (completed) {
        const parsed = JSON.parse(completed)
        const result = Array.isArray(parsed) ? parsed : []
        console.log('📊 Completed lessons for course', courseIdStr, ':', result)
        return result
      }
      console.log('⚠️ No completion data found for course:', courseIdStr)
      return []
    } catch (error) {
      console.error('❌ Error reading completed lessons:', error, 'courseId:', courseId)
      return []
    }
  }

  // Calculate completion percentage for a course
  const calculateCompletionPercentage = (course) => {
    const totalLessons = course.lessonCount || 0
    if (totalLessons === 0) return 0
    
    const courseId = course._id || course.id
    const completedLessons = getCompletedLessons(courseId)
    const completedCount = completedLessons.length
    
    return Math.round((completedCount / totalLessons) * 100)
  }

  const fetchCourses = async () => {
    try {
      setLoading(true)
      const response = await api.get('courses/my-courses')
      if (response.status === 200) {
        setCourses(response.data.courses || [])
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
      toast.error('Failed to fetch courses')
    } finally {
      setLoading(false)
    }
  }

  const handleCourseClick = (courseId) => {
    // Navigate to course - StudentLessonView will handle redirecting to first lesson
    navigate(`/student/courses/${courseId}`)
  }

  if (loading) {
    return (
      <div className="bg-white border rounded-[10px] border-[#F0F0F0] p-8">
        <div className="text-center">Loading courses...</div>
      </div>
    )
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0] p-0">
      {/* Header */}
      <div
        className="flex items-center border-b border-[#F0F0F0] px-6 py-4"
        style={{ justifyContent: 'space-between' }}
      >
        {/* Title */}
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-[#030303]">
            My Courses
          </h2>
        </div>
        {/* Empty center space for consistency */}
        <div className="flex-1 flex justify-center min-w-0">
        </div>
        {/* Summary right-aligned */}
        <div className="flex-1 flex justify-end min-w-0">
          <div className="text-[#535862] text-base font-medium">
            {courses.length} {courses.length === 1 ? 'course' : 'courses'} assigned
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {courses.length === 0 ? (
          <div className="bg-white border rounded-[10px] border-[#F0F0F0] p-12 text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium text-[#030303] mb-2">No courses assigned</p>
            <p className="text-sm text-[#535862]">
              You don't have any courses assigned yet. Contact your instructor to get enrolled.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div
                key={course._id}
                onClick={() => handleCourseClick(course._id)}
                className="bg-white border border-[#F0F0F0] rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              >
                {/* Course Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-[#DE3C7D] to-[#c8356f] overflow-hidden">
                  {course.thumbnail ? (
                    <img
                      src={course.thumbnail.startsWith('/uploads/') ? `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${course.thumbnail}` : course.thumbnail}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  {(!course.thumbnail || course.thumbnail === '') && (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}
                </div>

                {/* Course Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-[#030303] mb-2 line-clamp-2 group-hover:text-[#DE3C7D] transition-colors">
                    {course.title}
                  </h3>
                  <p className="text-sm text-[#535862] mb-3 line-clamp-2">
                    {course.description || 'No description available'}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-[#535862]">Progress</span>
                      <span className="text-xs font-semibold text-[#DE3C7D]">
                        {calculateCompletionPercentage(course)}%
                      </span>
                    </div>
                    <div className="w-full bg-[#F0F0F0] rounded-full h-2">
                      <div
                        className="bg-[#DE3C7D] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${calculateCompletionPercentage(course)}%` }}
                      />
                    </div>
                    <p className="text-xs text-[#535862] mt-1">
                      {getCompletedLessons(course._id || course.id).length} of {course.lessonCount || 0} lessons completed
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-[#535862]">
                      <Play className="w-4 h-4" />
                      <span>View Course</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#535862] group-hover:text-[#DE3C7D] transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export default StudentCourseView

